import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import { MISMATCHED_RESPONSES } from 'pricing-pages-ui-components/constants/ErrorCauses';
import { GlobalWebBrandingStyles } from 'pricing-pages-ui-components/stylesheets/GlobalWebBranding.styles';
import { SideNavOverrideStyles } from 'pricing-pages-ui-components/stylesheets/SideNavOverride.styles';
import { getRedirectQueryParamsForCheckout } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { useEffect, useRef } from 'react';
import { useLocation, withRouter } from 'react-router-dom';
import { getPrimaryTracker } from 'usage-tracker-container';
import { makeLoadable } from '../components/common/LoadableWrapper';
import { useDerivedCohortInfoContext } from '../context/DerivedCohortInfoContext';
import { useBulkPurchaseOptions } from '../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode, usePricingPageCurrencies } from '../hooks/useCurrencies';
import ErrorPage from '../pages/ErrorPage';
import { isHubSpotDotComAuthedUserRedirect } from '../utils/appUtils';
import { Metrics } from '../utils/metrics';
import { useQueryParamFromLocation } from '../utils/urls/appQueryParamUtilsLegacy';
import { useProductInventory } from '../v2/hooks/inventory/useProductInventory';
import { useIsBillingAdmin, useIsInAppAuth } from '../v2/hooks/user/useUserInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const withGlobalStylingOverrides = Component => {
  return props => {
    const theme = useTheme();
    const isTrellis = isTrellisTheme(theme);
    const ref = useRef(null);
    const {
      pathname
    } = useLocation();
    const isAppAuth = useIsInAppAuth();
    const isInterstitial = pathname.includes('interstitial');
    useEffect(() => {
      const bgColor = !isInterstitial ? theme.color['fill-surface-recessed'] : theme.color['fill-surface-default'];
      document.body.style.backgroundColor = bgColor;
      return () => {
        document.body.style.backgroundColor = '';
      };
    }, [theme, isInterstitial]);
    useEffect(() => {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView();
    }, [pathname]);
    const savedQueryParams = getRedirectQueryParamsForCheckout();
    const isFromDotCom = Object.keys(savedQueryParams).some(param => ['hubs_content', 'hubs_signup-url'].includes(param));
    const useBranding = isTrellis && (isFromDotCom || !isAppAuth);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [useBranding && /*#__PURE__*/_jsx(GlobalWebBrandingStyles, {}), isTrellis && /*#__PURE__*/_jsx(SideNavOverrideStyles, {}), /*#__PURE__*/_jsx("div", {
        ref: ref,
        "aria-hidden": true
      }), /*#__PURE__*/_jsx(Component, Object.assign({}, props))]
    });
  };
};
const LoadableBaseLayout = makeLoadable({
  loader: () => import( /* webpackChunkName: "BaseLayout" */'pricing-pages-ui/layouts/BaseLayout')
});
const AppLayout = ({
  children,
  location
}) => {
  const isBillingAdmin = useIsBillingAdmin();
  const isAppAuth = useIsInAppAuth();
  const currencyCode = useDisplayedCurrencyCode();
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);
  const isRedirect = isHubSpotDotComAuthedUserRedirect();
  useEffect(() => {
    getPrimaryTracker().setProperties({
      isBillingAdmin,
      upgradeSource,
      inAppOrPublic: isAppAuth ? 'in-app' : 'public',
      activeCurrencyCode: currencyCode,
      isDotComAuthedRedirect: isAppAuth && isRedirect
    });
    if (isRedirect) {
      Metrics.counter('redirected-from-dotcom').increment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePricingPageCurrencies({
    isAppAuth
  });
  useBulkPurchaseOptions();
  useProductInventory();
  const {
    hasMismatchedProductsAndPopData,
    mismatchedProductsAndPopDataMetrics,
    loading: isDerivedCohortInfoLoading
  } = useDerivedCohortInfoContext();
  if (isAppAuth && hasMismatchedProductsAndPopData && !isDerivedCohortInfoLoading) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: MISMATCHED_RESPONSES,
      additionalErrorLogging: mismatchedProductsAndPopDataMetrics
    });
  }
  if (isAppAuth) {
    return children;
  }

  /**
   * If we came from public hubspot.com/pricing render the dot com marketing nav.
   * This represents authenticated users who go to the website pricing pages
   * We want to preserve the dot com nav since otherwise it would lead to a weird
   * experience where they'd lose their nav and couldn't go back without using the
   * browser back button
   */

  return /*#__PURE__*/_jsx(LoadableBaseLayout, {
    isLoggedIn: isAppAuth,
    pathname: location.pathname,
    children: children
  });
};
export default withGlobalStylingOverrides(withRouter(AppLayout));